/**
 * LA IMAGEN DE CADA CANALIZACIÓN (PLAN_IMAGENES.md §0).
 *
 * Origen: la carpeta FOTO 1 de cada entidad, llevada al sitio con
 * `4 OPERACION Y SISTEMAS/SCRIPTS/_deepart_al_sitio.py --escribir`.
 *   · Jeshua, María, María Magdalena, Ana y Los Esenios: todos sus estilos de
 *     Deep Art (123-124 cada una), sin la foto base sin estilar.
 *   · Metatrón, Miguel, Gabriel y Rafael: su única foto (Faina, 01-10-2026:
 *     «debiera haber solo una foto»). Las demás voces no tienen todavía.
 *   · Trabajadores de la Luz: sus escenas de grupo. Sus piezas no llevan
 *     "Trabajadores de la Luz" en `entidad` (las vocea un ser calibrado), así
 *     que se reconocen por la sigla LW del id.
 *
 * CADA DÍA CAMBIA (Faina, 01-10-2026: «cuando cambie de día asigna
 * aleatoriamente la imagen a cada canalización», propias y de terceros). Dentro
 * del mismo día es siempre la misma —la cabecera de la ficha y el círculo del
 * mensaje del día coinciden, y recargar no la cambia—; al día siguiente sale
 * otra. El día es el de `deldia.js`, el mismo que elige el mensaje del día.
 *
 * 🔴 No toca `IMAGENES_VOZ` (imagenes.js), que sigue vacío a propósito: las
 * tarjetas, la página de Voces y la cuadrícula de la Biblioteca no cambian
 * (regla de Faina, 29-ago).
 */
import { numeroDeDia } from './deldia';

const cargar = (ctx) => ctx.keys().sort().map((k) => ctx(k));

const POR_ENTIDAD = {
  'Jeshua': cargar(require.context('../assets/deepart/jeshua', false, /\.webp$/)),
  'María': cargar(require.context('../assets/deepart/maria', false, /\.webp$/)),
  'María Magdalena': cargar(require.context('../assets/deepart/magdalena', false, /\.webp$/)),
  'Ana': cargar(require.context('../assets/deepart/ana', false, /\.webp$/)),
  'Los Esenios': cargar(require.context('../assets/deepart/esenios', false, /\.webp$/)),
  'Metatrón': cargar(require.context('../assets/deepart/metatron', false, /\.webp$/)),
  'Miguel': cargar(require.context('../assets/deepart/miguel', false, /\.webp$/)),
  'Gabriel': cargar(require.context('../assets/deepart/gabriel', false, /\.webp$/)),
  'Rafael': cargar(require.context('../assets/deepart/rafael', false, /\.webp$/)),
};

const TDL = cargar(require.context('../assets/deepart/tdl', false, /\.webp$/));

const esDeTdL = (pieza) => /^\d{4}LW/.test(pieza?.id || '');

const listaPara = (pieza) => {
  if (!pieza) return null;
  if (esDeTdL(pieza)) return TDL;
  return POR_ENTIDAD[pieza.entidad] || null;
};

/* Una mezcla de 32 bits bien repartida: con un hash simple, días seguidos de
   la misma pieza caerían en estilos vecinos de la lista. */
const mezcla = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 2246822507);
  h ^= h >>> 13;
  h = Math.imul(h, 3266489909);
  h ^= h >>> 16;
  return h >>> 0;
};

const indice = (clave, dia, n) => mezcla(`${clave}|${dia}`) % n;

/** La imagen de esta canalización hoy: la misma todo el día, otra mañana. */
export const imagenDelDia = (pieza, fecha = new Date()) => {
  const lista = listaPara(pieza);
  if (!lista || !lista.length) return null;
  if (lista.length === 1) return lista[0];
  const clave = pieza.id || pieza.slug || '';
  const dia = numeroDeDia(fecha);
  let i = indice(clave, dia, lista.length);
  // Que no repita la de ayer: al azar pasaría una vez cada ~124 días.
  if (i === indice(clave, dia - 1, lista.length)) i = (i + 1) % lista.length;
  return lista[i];
};
